import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  cancelReferral,
  getMyReferralCode,
  getMyReferralStats,
  getReferralAnalytics,
  getReferralDetail,
  getReferrals,
  getReferralSettings,
  updateReferralSettings,
} from "../services/referralService";
import {
  ReferralAdminListResponse,
  ReferralAnalytics,
  ReferralCodeInfo,
  ReferralDetailItem,
  ReferralFilters,
  ReferralSettings,
  ReferralStatsResponse,
  UpdateReferralSettingsRequest,
} from "../types/referral";
import { ApiResponse } from "../types/common";

export const referralKeys = {
  all: ["referral"] as const,
  myCode: () => [...referralKeys.all, "my-code"] as const,
  myStats: () => [...referralKeys.all, "my-stats"] as const,
  settings: () => [...referralKeys.all, "settings"] as const,
  analytics: () => [...referralKeys.all, "analytics"] as const,
  list: (filters?: ReferralFilters) =>
    [...referralKeys.all, "list", filters] as const,
  detail: (id: string) => [...referralKeys.all, "detail", id] as const,
};

export const useMyReferralCode = () => {
  return useQuery<ApiResponse<ReferralCodeInfo>>({
    queryKey: referralKeys.myCode(),
    queryFn: getMyReferralCode,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useMyReferralStats = () => {
  return useQuery<ApiResponse<ReferralStatsResponse>>({
    queryKey: referralKeys.myStats(),
    queryFn: getMyReferralStats,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useReferralSettings = () => {
  return useQuery<ApiResponse<ReferralSettings>>({
    queryKey: referralKeys.settings(),
    queryFn: getReferralSettings,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useReferralSettingsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateReferralSettingsRequest) =>
      updateReferralSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: referralKeys.settings() });
    },
  });
};

export const useReferrals = (filters: ReferralFilters) => {
  return useQuery<ApiResponse<ReferralAdminListResponse>>({
    queryKey: referralKeys.list(filters),
    queryFn: () => getReferrals(filters),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useReferralDetail = (id: string) => {
  return useQuery<ApiResponse<ReferralDetailItem>>({
    queryKey: referralKeys.detail(id),
    queryFn: () => getReferralDetail(id),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
};

export const useCancelReferral = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cancelReferral(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: referralKeys.all });
    },
  });
};

export const useReferralAnalytics = () => {
  return useQuery<ApiResponse<ReferralAnalytics>>({
    queryKey: referralKeys.analytics(),
    queryFn: getReferralAnalytics,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};
